import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useHasPermission } from 'hooks/usePermissions';
import { dineOpsService } from 'api/services/dineOpsService';
import SectionedModalShell, { ShellBlockers, ShellChip, type ShellBlocker, type ShellSection } from 'components/modals/common/SectionedModalShell';
import { DineOpsPhotoPanel } from './DineOpsPhotoPanel';
import { DineOpsPairPanel } from './DineOpsPairPanel';
import { loadExtraOptions, type ExtraOption } from './dineOpsExtras';
import {
  activeAt, kitchenMark, names, soldInMark, taxMark, unroutedAt, untaxedAt, type DishSection,
} from 'components/modules/dineops-module/masters/dishWorkspace';
import type {
  DineKitchenStation,
  DineTaxBasis,
  DineItem,
  DineItemOutlet,
  DineMenuGroup,
  DineTagAnswer,
  DineTag,
  DineItemVariant,
  DineItemAddon,
  TagAnswerInput,
} from 'api/types';

/**
 * The dish window (2026-10-28), like the outlet window: every part of a dish in one place — Details,
 * Kitchen, Tax, Sizes, Add-ons, Attributes, Photos, Goes well with, Sold in. Kitchen, Tax and Sold in
 * carry status marks checked against every outlet (of the user's) that sells the dish, by the Setup
 * Checklist's rules. Details, Kitchen and Tax save together with the window's Save; Sizes, Add-ons,
 * Attributes, Photos and Goes well with keep their own saves and say so.
 *
 * TAX (decided 2026-10-28): every line bills as restaurant service under the outlet's tax profile, so a
 * dish carries no rate here. Only Outside GST (liquor) is the dish's own answer. A dish stored as sold as
 * goods keeps its HSN and rate in the database for a later "sold as goods" scenario, untouched.
 *
 * THE ATTRIBUTES SECTION IS GENERATED, NOT WRITTEN
 * It renders whatever GET /menu/items/{id}/tags returns. Add a tag group in the
 * master and it appears here on every dish with no change to this file.
 *
 * WHY SECTIONS ARE LOCKED WHILE CREATING
 * Sizes, Add-ons, Attributes, Photos and pairs all hang off an item id, and a dish
 * being created has none. Rather than buffer them in memory and replay several calls
 * after save — where a later failure leaves a dish saved with half its detail and
 * no way to tell which half — the dish is saved first and the sections unlock.
 */

interface DineOpsItemModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DineItem | null;
  groups: DineMenuGroup[];
  onSave: () => void;
  onClose: () => void;
  /** Edit: the section to open on (a grid cell). */
  initialSection?: DishSection;
  /** Opens Outlet Items, where each outlet's price, station and listing are set. Absent: no access. */
  onOpenOutletItems?: () => void;
}

interface DetailsForm {
  itemCode: string;
  itemName: string;
  groupId: string;
  price: string;
  isOpenPrice: boolean;
  /** Requisition transfer value of one portion (R1). '' = the outlet's percentage of the price. */
  transferPrice: string;
  minutesToTable: string;
  /** '' = follow the group's station. */
  stationId: string;
  /** '' = follow the group; 'yes' / 'no' = the dish answers for itself. */
  allowFallback: '' | 'yes' | 'no';
  isActive: boolean;
  /** No GST on the dish (liquor). Everything else bills as restaurant service. */
  outsideGst: boolean;
}

const EMPTY_DETAILS: DetailsForm = {
  itemCode: '',
  itemName: '',
  groupId: '',
  price: '',
  isOpenPrice: false,
  transferPrice: '',
  minutesToTable: '',
  stationId: '',
  allowFallback: '',
  isActive: true,
  outsideGst: false,
};

/** Fields of each section that save with the window, for the red count on the nav. */
const DETAIL_FIELDS = ['itemCode', 'itemName', 'groupId', 'price', 'transferPrice', 'minutesToTable'];

/** A size row being edited. Kept as strings so a half-typed price is not NaN. A size is an item (DO-314). */
interface VariantRow {
  id?: string;
  itemCode: string;
  variantName: string;
  price: string;
  isDefault: boolean;
}

/** An add-on row: the extra item picked (DO-315). Name and price are shown from the item. */
interface AddonRow {
  id?: string;
  addonItemId: string;
}

const hint: React.CSSProperties = { fontSize: 12, opacity: 0.7, marginTop: 4 };
const cell: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--border-color, #e5e7eb)', textAlign: 'left' };

const DineOpsItemModal: React.FC<DineOpsItemModalProps> = ({
  isOpen, mode, record, groups, onSave, onClose, initialSection = 'details', onOpenOutletItems,
}) => {
  const [section, setSection] = useState<DishSection>('details');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [details, setDetails] = useState<DetailsForm>(EMPTY_DETAILS);
  /** Details, Kitchen and Tax as last loaded or saved: the window's Save is for changes to these. */
  const snapshotRef = useRef('');
  /** How the dish is taxed in the database. Sent back only when Outside GST is switched. */
  const basisRef = useRef<DineTaxBasis>('SERVICE');

  /** The dish's id. Null while creating, which is what locks the other sections. */
  const [itemId, setItemId] = useState<string | null>(null);
  const [rowVersion, setRowVersion] = useState(0);
  /** Outlets (of the user's) that sell the dish. Null until loaded, or while creating. */
  const [outlets, setOutlets] = useState<DineItemOutlet[] | null>(null);

  const [answers, setAnswers] = useState<DineTagAnswer[]>([]);
  /**
   * Tag groups the user has actually touched on this visit.
   *
   * ONLY THESE ARE SENT. Sending every group would rewrite an INHERITED answer
   * as the dish's own: the values look identical afterwards, so nothing appears
   * to have happened, but the link to the menu group is severed and changing the
   * group later no longer reaches the dish.
   */
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [sizesDirty, setSizesDirty] = useState(false);
  /** Sizes the dish has saved: with any, its price is its default size's and it cannot be open-priced. */
  const [sizeCount, setSizeCount] = useState(0);
  /** Why the last save was refused, shown above every section. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const fail = (message: string) => { setSaveError(message); notificationService.toast.error(message); };
  const done = (message: string) => { setSaveError(null); notificationService.toast.success(message); };
  /** Items an add-on can be picked from (DO-315): Extras first. */
  const [extraOptions, setExtraOptions] = useState<ExtraOption[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [addonsDirty, setAddonsDirty] = useState(false);
  const [addonSource, setAddonSource] = useState<'OWN' | 'GROUP' | 'NONE'>('NONE');
  const [addonGroupName, setAddonGroupName] = useState<string | undefined>();
  const [loadingSection, setLoadingSection] = useState(false);
  /** Sections loaded for this dish: a second visit keeps unsaved edits rather than reloading. */
  const loadedRef = useRef<Set<DishSection>>(new Set());
  const [discardOpen, setDiscardOpen] = useState(false);
  /** Menu Items' 'tax' action (DineOps tax T10): changing how a dish is taxed. The server checks it too. */
  const canTax = useHasPermission('dineops-menu', 'tax');
  const [stations, setStations] = useState<DineKitchenStation[]>([]);

  const loadOutlets = useCallback((id: string) => {
    dineOpsService.getItemOutlets(id).then(setOutlets).catch(() => setOutlets([]));
  }, []);

  // --------------------------------------------------------------- open / reset

  // Reset when the window opens for a dish. The screen keeps the record it opened with, so a list
  // refresh after a save does not come back here and drop unsaved edits in other sections.
  useEffect(() => {
    if (!isOpen) return;

    setSaveError(null);
    setErrors({});
    loadedRef.current = new Set();
    setTouched(new Set());
    setSizesDirty(false);
    setAddonsDirty(false);
    dineOpsService.getStations({ pageSize: 100 }).then((r) => setStations(r.data)).catch(() => setStations([]));

    if (mode === 'edit' && record) {
      const d: DetailsForm = {
        itemCode: record.itemCode,
        itemName: record.itemName,
        groupId: String(record.groupId),
        // An open-priced dish shows a blank price, not 0.00 — it has none.
        price: record.isOpenPrice ? '' : String(record.price),
        isOpenPrice: record.isOpenPrice,
        transferPrice: record.transferPrice != null ? String(record.transferPrice) : '',
        minutesToTable: record.minutesToTable != null ? String(record.minutesToTable) : '',
        stationId: record.ownStationId ?? '',
        allowFallback: record.allowFallback == null ? '' : record.allowFallback ? 'yes' : 'no',
        isActive: record.isActive,
        outsideGst: record.taxBasis === 'OUTSIDE_GST',
      };
      setDetails(d);
      snapshotRef.current = JSON.stringify(d);
      basisRef.current = record.taxBasis ?? 'SERVICE';
      setItemId(record.id);
      setRowVersion(record.rowVersion);
      setSizeCount(record.variantCount ?? 0);
      setOutlets(null);
      loadOutlets(record.id);
      setSection(initialSection);
      void loadSection(initialSection, record.id);
    } else {
      setSizeCount(0);
      setDetails(EMPTY_DETAILS);
      snapshotRef.current = JSON.stringify(EMPTY_DETAILS);
      basisRef.current = 'SERVICE';
      setItemId(null);
      setRowVersion(0);
      setOutlets(null);
      setAnswers([]);
      setVariants([]);
      setAddons([]);
      setSection('details');
    }
  }, [isOpen, mode, record]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------- lazy section loads

  const loadSection = useCallback(async (next: DishSection, id: string | null = itemId) => {
    if (!id || loadedRef.current.has(next) || !['attributes', 'variants', 'addons'].includes(next)) return;
    loadedRef.current.add(next);

    setLoadingSection(true);
    try {
      if (next === 'attributes') {
        setAnswers(await dineOpsService.getItemTags(id));
        setTouched(new Set());
      } else if (next === 'variants') {
        const list = await dineOpsService.getVariants(id);
        setVariants(list.map((v: DineItemVariant) => ({
          id: v.id, itemCode: v.itemCode ?? '', variantName: v.variantName, price: String(v.price), isDefault: v.isDefault,
        })));
        setSizesDirty(false);
      } else if (next === 'addons') {
        loadExtraOptions(id).then(setExtraOptions).catch(() => setExtraOptions([]));
        const resolved = await dineOpsService.getItemAddons(id);
        setAddonSource(resolved.source);
        setAddonGroupName(resolved.addonGroupName);
        // Only the dish's OWN extras are editable. What it inherits is shown
        // read-only, because editing it here would silently fork the shared set.
        setAddons(
          resolved.source === 'OWN'
            ? resolved.addons.map((a: DineItemAddon) => ({ id: a.id, addonItemId: a.addonItemId ?? '' }))
            : [],
        );
        setAddonsDirty(false);
      }
    } catch {
      loadedRef.current.delete(next);
      fail('That section could not be loaded. Try again.');
    } finally {
      setLoadingSection(false);
    }
  }, [itemId]);

  const goTo = useCallback((next: DishSection) => {
    setSection(next);
    void loadSection(next);
  }, [loadSection]);

  // ------------------------------------------------------------------- details

  const groupStation = useMemo(
    () => groups.find((g) => g.id === details.groupId)?.stationName,
    [groups, details.groupId],
  );
  const groupStationLabel = groupStation ? `Follow the group (${groupStation})` : 'Follow the group';
  const stationOptions = useMemo(
    () => [{ value: '', label: groupStationLabel }, ...stations.map((s) => ({ value: s.id, label: s.stationName }))],
    [stations, groupStationLabel],
  );
  // An empty value shows the placeholder, so the placeholder carries the group's answer too.
  const groupFallbackLabel = `Follow the group (${groups.find((x) => x.id === details.groupId)?.allowFallback === false ? 'no' : 'yes'})`;
  const fallbackOptions = useMemo(() => [
    { value: '', label: groupFallbackLabel },
    { value: 'yes', label: 'Yes, the fallback can make it' },
    { value: 'no', label: 'No, show it unavailable' },
  ], [groupFallbackLabel]);

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: String(g.id), label: `${g.courseName} · ${g.groupName}` })),
    [groups],
  );

  const detailsDirty = JSON.stringify(details) !== snapshotRef.current;

  const validateDetails = useCallback((): boolean => {
    const next: Record<string, string> = {};

    // A new dish may leave the code blank: the API makes one from the name
    // (BUT-001). Once the dish exists its code is what bills know it by.
    if (itemId && !details.itemCode.trim()) next.itemCode = 'A dish needs a code.';
    if (!details.itemName.trim()) next.itemName = 'A dish needs a name.';
    if (!details.groupId) next.groupId = 'Choose the menu group this dish belongs to.';

    if (details.isOpenPrice) {
      // The API refuses an open-priced dish that also carries a list price.
      if (details.price.trim() && Number(details.price) !== 0) {
        next.price = 'An open-priced dish has no list price. Clear it, or turn off open pricing.';
      }
    } else {
      const p = Number(details.price);
      if (!details.price.trim() || Number.isNaN(p)) next.price = 'Enter a price.';
      else if (p < 0) next.price = 'A price cannot be negative.';
    }

    if (!details.isOpenPrice && details.transferPrice.trim()) {
      const t = Number(details.transferPrice);
      if (Number.isNaN(t) || t < 0) next.transferPrice = 'A transfer price is 0 or more, or blank for the outlet\'s percentage.';
    }

    if (details.minutesToTable.trim()) {
      const m = Number(details.minutesToTable);
      if (Number.isNaN(m) || m <= 0) next.minutesToTable = 'Time to table is a number of minutes.';
    }

    setErrors(next);
    if (Object.keys(next).length > 0) setSection('details');
    return Object.keys(next).length === 0;
  }, [details, itemId]);

  const saveDetails = useCallback(async () => {
    if (!validateDetails()) return;

    setSaving(true);
    try {
      // Tax is sent only when Outside GST is switched; otherwise the dish keeps what it has (a dish
      // stored as goods keeps its HSN). Restaurant service needs no rate: the outlet's profile taxes it.
      const wasOutside = basisRef.current === 'OUTSIDE_GST';
      const basis: DineTaxBasis = details.outsideGst ? 'OUTSIDE_GST' : wasOutside ? 'SERVICE' : basisRef.current;
      const body = {
        groupId: details.groupId,
        itemCode: details.itemCode.trim(),
        itemName: details.itemName.trim(),
        price: details.isOpenPrice ? 0 : Number(details.price),
        isOpenPrice: details.isOpenPrice,
        // An open-priced dish is never on a requisition, so it keeps no transfer price.
        transferPrice: !details.isOpenPrice && details.transferPrice.trim() ? Number(details.transferPrice) : null,
        minutesToTable: details.minutesToTable.trim() ? Number(details.minutesToTable) : undefined,
        stationId: details.stationId || undefined,
        allowFallback: details.allowFallback === '' ? undefined : details.allowFallback === 'yes',
        tax: details.outsideGst !== wasOutside
          ? { taxBasis: basis, rateCode: '', taxTreatment: 'TAXABLE' as const, priceIncludesTax: null }
          : undefined,
      };

      if (itemId) {
        const saved = await dineOpsService.updateItem(itemId, { ...body, isActive: details.isActive, rowVersion });
        setRowVersion(saved.rowVersion);
        done(`'${saved.itemName}' updated.`);
        loadOutlets(itemId);   // a station change changes where it can be sent
      } else {
        const created = await dineOpsService.createItem(body);
        // Hold the new id so the other sections open without a reload.
        setItemId(created.id);
        setRowVersion(created.rowVersion);
        loadOutlets(created.id);
        // Show the code the system chose when the field was left blank.
        const withCode = { ...details, itemCode: created.itemCode };
        setDetails(withCode);
        snapshotRef.current = JSON.stringify(withCode);
        done(`'${created.itemName}' is on the menu. List it on Outlet Items to sell it; its sizes, extras and attributes can be set now.`);
      }
      basisRef.current = basis;
      if (itemId) snapshotRef.current = JSON.stringify(details);
      onSave();
    } catch (err: any) {
      fail(err?.response?.data?.message ?? 'The dish could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [details, itemId, rowVersion, validateDetails, onSave, loadOutlets]);

  // ---------------------------------------------------------------- attributes

  /** Sets a dish's answer for one group, locally. Saved by saveAttributes. */
  const setAnswer = useCallback((tagGroupId: string, tagIds: string[], isNone: boolean) => {
    setTouched((prev) => new Set(prev).add(tagGroupId));
    setAnswers((prev) => prev.map((a) => {
      if (a.tagGroupId !== tagGroupId) return a;
      const chosen = a.options.filter((o) => tagIds.includes(o.id));
      return {
        ...a,
        answers: chosen,
        // Mirrors how the API will resolve it, so the section shows the outcome before the save.
        source: isNone ? 'None' : chosen.length > 0 ? 'Own' : (a.isInherited ? 'Inherited' : 'Unanswered'),
      };
    }));
  }, []);

  const saveAttributes = useCallback(async () => {
    if (!itemId) return;
    const payload: TagAnswerInput[] = answers
      .filter((a) => touched.has(a.tagGroupId))
      .map((a) => ({
        tagGroupId: a.tagGroupId,
        tagIds: a.source === 'None' ? [] : a.answers.map((t) => t.id),
        isNone: a.source === 'None',
      }));
    if (payload.length === 0) { done('No attribute was changed.'); return; }

    setSaving(true);
    try {
      setAnswers(await dineOpsService.saveItemTags(itemId, payload));
      setTouched(new Set());
      done(`${payload.length} attribute(s) saved. Food type, item type and availability are written to the dish from their tags.`);
      onSave();
    } catch (err: any) {
      fail(err?.response?.data?.message ?? 'The attributes could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [itemId, answers, touched, onSave]);

  // ------------------------------------------------------------------ variants

  const editVariants = (fn: (p: VariantRow[]) => VariantRow[]) => { setVariants(fn); setSizesDirty(true); };

  const saveVariants = useCallback(async () => {
    if (!itemId) return;

    // The API enforces these too; saying it here avoids a round trip to be told.
    if (variants.some((v) => !v.variantName.trim())) return fail('Every size needs a name, e.g. Half. Remove the empty row or fill it in.');
    if (variants.some((v) => v.price.trim() === '' || Number(v.price) < 0)) return fail('Every size needs a price of 0 or more.');
    if (variants.length === 1) return fail('A dish has no sizes, or at least two. One size is a choice with a single answer — set the dish price instead.');
    if (variants.length > 1 && variants.filter((v) => v.isDefault).length !== 1) {
      return fail('Exactly one size must be the default — it is what the waiter gets without choosing.');
    }

    setSaving(true);
    try {
      const saved = await dineOpsService.saveVariants(
        itemId,
        variants.map((v) => ({
          id: v.id, itemCode: v.itemCode.trim() || undefined, variantName: v.variantName.trim(),
          price: Number(v.price) || 0, isDefault: v.isDefault,
        })),
      );
      setVariants(saved.map((v) => ({
        id: v.id, itemCode: v.itemCode ?? '', variantName: v.variantName, price: String(v.price), isDefault: v.isDefault,
      })));
      setSizesDirty(false);
      setSizeCount(saved.length);
      // The dish's price follows its default size; the snapshot moves with it so Save is not lit by it.
      const def = saved.find((v) => v.isDefault);
      if (def) {
        setDetails((p) => {
          const next = { ...p, price: String(def.price) };
          const snap = JSON.parse(snapshotRef.current || '{}');
          snapshotRef.current = JSON.stringify({ ...snap, price: String(def.price) });
          return next;
        });
      }
      done('Sizes saved.');
      onSave();
    } catch (err: any) {
      fail(err?.response?.data?.message ?? 'The sizes could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [itemId, variants, onSave]);

  // ------------------------------------------------------------------- add-ons

  const editAddons = (fn: (p: AddonRow[]) => AddonRow[]) => { setAddons(fn); setAddonsDirty(true); };

  const saveAddons = useCallback(async () => {
    if (!itemId) return;

    setSaving(true);
    try {
      const resolved = await dineOpsService.saveItemAddons(
        itemId,
        addons.filter((a) => a.addonItemId).map((a) => ({ id: a.id, addonItemId: a.addonItemId })),
      );
      setAddonSource(resolved.source);
      setAddonGroupName(resolved.addonGroupName);
      setAddons(resolved.source === 'OWN' ? resolved.addons.map((a) => ({ id: a.id, addonItemId: a.addonItemId ?? '' })) : []);
      setAddonsDirty(false);
      done(resolved.source === 'OWN'
        ? 'This dish now offers its own extras.'
        : 'Cleared. This dish follows its menu group’s set again.');
      onSave();
    } catch (err: any) {
      fail(err?.response?.data?.message ?? 'The extras could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [itemId, addons, onSave]);

  if (!isOpen) return null;

  const locked = itemId === null;
  const anyDirty = detailsDirty || sizesDirty || addonsDirty || touched.size > 0;
  const handleClose = () => { if (anyDirty) setDiscardOpen(true); else onClose(); };

  // --------------------------------------------------------------------- render

  const err = (k: string) => errors[k] && <div className="form-error-message">{errors[k]}</div>;
  const ownSave = (what: string, label: string, onClick: () => void, dirty: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div className="setup-sheet-note" style={{ flex: 1 }}>{what} save on their own, here; the window&rsquo;s Save is for Details, Kitchen and Tax.</div>
      <button className="btn btn-primary btn-sm" onClick={onClick} disabled={saving || loadingSection || !dirty}>{label}</button>
    </div>
  );
  const lockedNote = (
    <div className="setup-sheet-note" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <IconSystem.lock size={14} /> Save the dish first: this part attaches to it. Press <strong>Save and continue</strong> below.
    </div>
  );
  const live = outlets ?? [];
  const unrouted = unroutedAt(live);
  const untaxed = untaxedAt(live, details.outsideGst);
  const sellingCount = activeAt(live).length;

  const detailsContent = (
    <>
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Code {itemId && <span className="text-danger">*</span>}</label>
          <input
            type="text"
            className={`form-input${errors.itemCode ? ' form-field-error' : ''}`}
            value={details.itemCode}
            onChange={(e) => setDetails((p) => ({ ...p, itemCode: e.target.value }))}
            placeholder={itemId ? 'e.g., BUT-001' : 'Leave blank to generate, or type your own'}
            maxLength={20}
            autoFocus
          />
          {err('itemCode')}
          {!itemId && !errors.itemCode && (
            <div style={hint}>Blank: three letters from the name plus a number, e.g. Butter Chicken → BUT-001.</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Dish name <span className="text-danger">*</span></label>
          <input
            type="text"
            className={`form-input${errors.itemName ? ' form-field-error' : ''}`}
            value={details.itemName}
            onChange={(e) => setDetails((p) => ({ ...p, itemName: e.target.value }))}
            placeholder="e.g., Sweet Corn Soup"
            maxLength={200}
          />
          {err('itemName')}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Menu group <span className="text-danger">*</span></label>
        <SyutSelect
          options={groupOptions}
          value={details.groupId}
          onChange={(v) => setDetails((p) => ({ ...p, groupId: (v as string) || '' }))}
          placeholder="Choose a category and group"
          size="sm"
        />
        {err('groupId')}
        <div style={hint}>The group decides the kitchen station and time to table, unless the dish says otherwise.</div>
      </div>

      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Price</label>
          <input
            type="number"
            className={`form-input${errors.price ? ' form-field-error' : ''}`}
            value={details.price}
            onChange={(e) => setDetails((p) => ({ ...p, price: e.target.value }))}
            disabled={details.isOpenPrice || sizeCount > 0}
            placeholder={details.isOpenPrice ? 'Entered at order time' : '0.00'}
            min={0}
            step="0.01"
          />
          {err('price')}
          <div style={hint} data-size-price-note={sizeCount > 0 ? '' : undefined}>
            {sizeCount > 0
              ? `This dish has ${sizeCount} sizes; its price is the default size's. Change prices in Sizes.`
              : 'The menu price. Each outlet can charge its own on Outlet Items.'}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Time to table (minutes)</label>
          <input
            type="number"
            className={`form-input${errors.minutesToTable ? ' form-field-error' : ''}`}
            value={details.minutesToTable}
            onChange={(e) => setDetails((p) => ({ ...p, minutesToTable: e.target.value }))}
            placeholder="Leave blank to follow the group"
            min={1}
          />
          {err('minutesToTable')}
        </div>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={details.isOpenPrice}
            disabled={sizeCount > 0}
            // Turning it on clears the price: the two cannot both hold.
            onChange={(e) => setDetails((p) => ({ ...p, isOpenPrice: e.target.checked, price: e.target.checked ? '' : p.price }))}
          />
          <span>Open priced — no fixed price; the manager or chef sets it when the order is taken</span>
        </label>
        <div style={hint}>
          For dishes like fish that are priced daily. Setting the price needs the set-price right, and who set it is
          recorded on the line. It is not remembered for the next sale.
        </div>
      </div>

      {!details.isOpenPrice && (
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Requisition transfer price</label>
            <input
              type="number"
              className={`form-input${errors.transferPrice ? ' form-field-error' : ''}`}
              value={details.transferPrice}
              onChange={(e) => setDetails((p) => ({ ...p, transferPrice: e.target.value }))}
              placeholder="Blank = the outlet's percentage of the price"
              min={0}
              step="0.01"
              data-transfer-price
            />
            {err('transferPrice')}
            <div style={hint}>
              What one portion is worth when a department takes it on a requisition (staff meal, welcome drink). No tax.
              {sizeCount > 0 ? ' Sizes use the outlet\'s percentage of their own price.' : ''}
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={details.isActive} onChange={(e) => setDetails((p) => ({ ...p, isActive: e.target.checked }))} />
            <span>Active on the menu</span>
          </label>
        </div>
      )}
    </>
  );

  const kitchenContent = (
    <>
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Kitchen station</label>
          <SyutSelect
            options={stationOptions}
            value={details.stationId}
            onChange={(v) => setDetails((p) => ({ ...p, stationId: (v as string) || '' }))}
            placeholder={groupStationLabel}
            size="sm"
          />
          <div style={hint}>Only when this dish is made somewhere else from the rest of its group. An outlet can send it elsewhere on Outlet Items.</div>
        </div>
        <div className="form-group">
          <label className="form-label">If its kitchen station is closed</label>
          <SyutSelect
            options={fallbackOptions}
            value={details.allowFallback}
            onChange={(v) => setDetails((p) => ({ ...p, allowFallback: ((v as string) || '') as DetailsForm['allowFallback'] }))}
            placeholder={groupFallbackLabel}
            size="sm"
          />
          <div style={hint}>Whether the fallback station can make it.</div>
        </div>
      </div>
      {!locked && live.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-kitchen-outlets>
          <thead><tr><th style={cell}>Outlet</th><th style={cell}>Sent to</th><th style={cell}>Status</th></tr></thead>
          <tbody>
            {live.map((o) => (
              <tr key={o.dineOutletId}>
                <td style={cell}>{o.outletName}</td>
                <td style={cell}>{o.isTicketable ? (o.effectiveStationName ?? '—') : 'No kitchen ticket'}{o.stationName ? ' (this outlet\'s own)' : ''}</td>
                <td style={cell}>
                  {!o.isActive ? <span style={{ color: 'var(--text-muted)' }}>Not on its card</span>
                    : o.isTicketable && !o.stationAllowed
                      ? <span className="text-danger">The outlet does not use this station: orders are refused. Tick it in the outlet&rsquo;s Kitchen stations.</span>
                      : 'OK'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!locked && outlets && live.length === 0 && <div className="setup-sheet-note">No outlet sells this dish yet, so there is nothing to check here.</div>}
    </>
  );

  const taxContent = (
    <>
      <div className="setup-sheet-note" style={{ marginBottom: 10 }}>
        {details.outsideGst
          ? 'No GST on this dish (alcohol for human consumption). State VAT on liquor is not charged yet.'
          : 'Billed as restaurant service under each outlet\'s tax profile: its SAC and GST rate are set on the outlet (Setup › Restaurant › Outlets › Tax), not on the dish.'}
      </div>
      {basisRef.current === 'GOODS' && !details.outsideGst && (
        <div className="setup-sheet-note" style={{ marginBottom: 10 }} data-goods-note>
          This dish is recorded as sold as goods{record?.taxSacHsnCode ? ` (HSN ${record.taxSacHsnCode})` : ''}. For now it is billed as
          restaurant service like every other dish; its HSN and rate are kept.
        </div>
      )}
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            data-outside-gst
            checked={details.outsideGst}
            disabled={!canTax}
            onChange={(e) => setDetails((p) => ({ ...p, outsideGst: e.target.checked }))}
          />
          <span>Outside GST — no GST is charged on this dish (liquor)</span>
        </label>
        {!canTax && <div style={hint}>Changing how a dish is taxed needs the Menu Items &lsquo;Tax&rsquo; right.</div>}
      </div>
      {!locked && !details.outsideGst && live.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-tax-outlets>
          <thead><tr><th style={cell}>Outlet</th><th style={cell}>Tax</th></tr></thead>
          <tbody>
            {live.map((o) => (
              <tr key={o.dineOutletId}>
                <td style={cell}>{o.outletName}</td>
                <td style={cell}>
                  {!o.taxOn ? <span style={{ color: 'var(--text-muted)' }}>Tax off for its property: no tax on bills</span>
                    : o.hasTaxProfile ? 'Taxed under the outlet\'s tax profile'
                      : <span className="text-danger">No tax profile: its bills are refused. Set the outlet&rsquo;s tax profile.</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const variantsContent = locked ? lockedNote : (
    <>
      {ownSave('Sizes', 'Save sizes', () => void saveVariants(), sizesDirty)}
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
        Each size is an item of its own, with its own code, so reports count Half and Full separately. It takes this
        dish&rsquo;s category, group, food type and tax. A dish has no sizes, or at least two with exactly one default.
        The price here is the standard price; each outlet can set its own on its Outlet Items list.
      </div>
      {loadingSection && <div style={{ padding: 8 }}>Loading…</div>}
      {variants.map((v, i) => (
        <div key={i} data-size-row style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.4fr)', gap: 8, alignItems: 'end', marginBottom: 8 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Size</label>
            <input type="text" className="form-input" value={v.variantName} placeholder="e.g., Half"
              onChange={(e) => editVariants((p) => p.map((x, j) => (j === i ? { ...x, variantName: e.target.value } : x)))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Item code</label>
            <input type="text" className="form-input" value={v.itemCode} maxLength={50}
              onChange={(e) => editVariants((p) => p.map((x, j) => (j === i ? { ...x, itemCode: e.target.value.toUpperCase() } : x)))}
              placeholder={details.itemCode && v.variantName.trim()
                ? `${details.itemCode}-${v.variantName.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}`
                : 'Made from the dish code'} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
              <label className="form-label">Price</label>
              <input type="number" className="form-input" value={v.price} min={0} step="0.01"
                onChange={(e) => editVariants((p) => p.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
              <input type="radio" name="variant-default" checked={v.isDefault}
                onChange={() => editVariants((p) => p.map((x, j) => ({ ...x, isDefault: j === i })))} />
              <span>Default</span>
            </label>
            <button className="btn btn-secondary" style={{ marginBottom: 4 }} title="Remove this size"
              onClick={() => editVariants((p) => p.filter((_, j) => j !== i))}>
              <IconSystem.trash size={14} />
            </button>
          </div>
        </div>
      ))}
      <button className="btn btn-secondary"
        onClick={() => editVariants((p) => [...p, { itemCode: '', variantName: '', price: '', isDefault: p.length === 0 }])}>
        <IconSystem.add size={14} /> Add a size
      </button>
    </>
  );

  const addonsContent = locked ? lockedNote : (
    <>
      {ownSave('Extras', 'Save extras', () => void saveAddons(), addonsDirty)}
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
        {addonSource === 'GROUP' && (
          <>This dish follows its menu group&rsquo;s set{addonGroupName ? ` — ${addonGroupName}` : ''}. Add a row below to give it
            its own extras instead; clear them all to go back to following the group.</>
        )}
        {addonSource === 'OWN' && <>This dish offers its own extras, not its menu group&rsquo;s set.</>}
        {addonSource === 'NONE' && <>Neither this dish nor its menu group offers any extras.</>}
        {' '}Pick each extra from the menu, usually the Extras category; its name and price come from that item.
      </div>
      {loadingSection && <div style={{ padding: 8 }}>Loading…</div>}
      {addons.map((a, i) => (
        <div key={i} data-addon-row style={{ display: 'flex', gap: 8, alignItems: 'end', marginBottom: 8 }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
            <SyutSelect
              label="Extra"
              options={extraOptions.filter((o) => o.value === a.addonItemId || !addons.some((x) => x.addonItemId === o.value))}
              value={a.addonItemId}
              onChange={(v) => editAddons((p) => p.map((x, j) => (j === i ? { ...x, addonItemId: (v as string) || '' } : x)))}
              placeholder="Pick an extra"
              size="sm"
              menuPortalTarget={document.body}
            />
          </div>
          <button className="btn btn-secondary" style={{ marginBottom: 4 }} title="Remove this extra"
            onClick={() => editAddons((p) => p.filter((_, j) => j !== i))}>
            <IconSystem.trash size={14} />
          </button>
        </div>
      ))}
      <button className="btn btn-secondary" onClick={() => editAddons((p) => [...p, { addonItemId: '' }])}>
        <IconSystem.add size={14} /> Add an extra
      </button>
    </>
  );

  const attributesContent = locked ? lockedNote : (
    <>
      {ownSave('Attributes', 'Save attributes', () => void saveAttributes(), touched.size > 0)}
      {loadingSection && <div style={{ padding: 8 }}>Loading…</div>}
      {!loadingSection && answers.length === 0 && <div style={{ padding: 8, opacity: 0.75 }}>No tag groups are configured.</div>}
      {!loadingSection && answers.map((a) => (
        <div key={a.tagGroupId} className="form-group" style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-color, #eee)' }}>
          <label className="form-label">
            {a.tagGroupName}
            {a.isDanger && <span style={{ color: 'var(--color-danger, #c0392b)', marginLeft: 6 }}>allergen</span>}
            <SourceBadge source={a.source} inheritable={a.isInherited} />
          </label>
          {a.hint && <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{a.hint}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {a.options.map((opt: DineTag) => {
              const chosen = a.answers.some((x) => x.id === opt.id);
              return (
                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type={a.selectMode === 'ONE' ? 'radio' : 'checkbox'}
                    name={`tg-${a.tagGroupId}`}
                    checked={chosen}
                    onChange={(e) => {
                      if (a.selectMode === 'ONE') return setAnswer(a.tagGroupId, [opt.id], false);
                      const next = e.target.checked
                        ? [...a.answers.map((x) => x.id), opt.id]
                        : a.answers.map((x) => x.id).filter((id) => id !== opt.id);
                      setAnswer(a.tagGroupId, next, false);
                    }}
                  />
                  <span>{opt.tagName}</span>
                </label>
              );
            })}
            {/*
              "None" is offered only for an inherited group, because only there is there something
              to refuse. It is NOT the same as leaving the question blank: blank inherits, None does not.
            */}
            {a.isInherited && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                <input type="checkbox" checked={a.source === 'None'} onChange={(e) => setAnswer(a.tagGroupId, [], e.target.checked)} />
                <span title="This dish has none of these, and must not inherit the group's answer">None — and do not inherit</span>
              </label>
            )}
          </div>
        </div>
      ))}
    </>
  );

  const soldInContent = locked ? lockedNote : (
    <>
      <div className="setup-sheet-note" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>
          The menu is kept once for every outlet; each outlet lists the dishes it sells, at its own price and station, on
          Outlet Items (for the outlet chosen in the header). Shown here: the outlets you work in.
        </span>
        {onOpenOutletItems && <button className="btn btn-secondary btn-sm" onClick={onOpenOutletItems}>Open Outlet Items</button>}
      </div>
      {!outlets && <div style={{ padding: 8 }}>Loading…</div>}
      {outlets && live.length === 0 && <div className="setup-sheet-note">No outlet sells this dish, so it is on no card. List it on Outlet Items.</div>}
      {live.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-sold-in>
          <thead><tr><th style={cell}>Outlet</th><th style={cell}>Price</th><th style={cell}>Kitchen station</th><th style={cell}>On its card</th></tr></thead>
          <tbody>
            {live.map((o) => (
              <tr key={o.dineOutletId}>
                <td style={cell}>{o.outletName} <span style={{ color: 'var(--text-muted)' }}>({o.outletCode})</span></td>
                <td style={cell}>{details.isOpenPrice ? 'Open' : o.effectivePrice.toFixed(2)}{o.price != null ? ' (its own)' : ''}</td>
                <td style={cell}>{o.isTicketable ? (o.effectiveStationName ?? '—') : '—'}</td>
                <td style={cell}>{!o.isActive ? 'Inactive' : o.isSoldOutHere ? 'Sold out today' : 'Yes'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const detailErrors = DETAIL_FIELDS.filter((f) => errors[f]).length;
  const sections: ShellSection[] = [
    { id: 'details', title: 'Details', errorCount: detailErrors,
      badge: details.itemName.trim() && details.groupId && (details.isOpenPrice || details.price.trim()) ? 'ok' : undefined, content: detailsContent },
    { id: 'kitchen', title: 'Kitchen', badge: kitchenMark(outlets), content: kitchenContent },
    { id: 'tax', title: 'Tax', badge: taxMark(outlets, details.outsideGst), content: taxContent },
    { id: 'variants', title: 'Sizes', badge: sizeCount > 0 ? 'ok' : undefined, content: variantsContent },
    { id: 'addons', title: 'Add-ons', content: addonsContent },
    { id: 'attributes', title: 'Attributes', content: attributesContent },
    { id: 'photos', title: 'Photos', content: locked ? lockedNote : itemId && <DineOpsPhotoPanel itemId={itemId} /> },
    { id: 'pairs', title: 'Goes well with', content: locked ? lockedNote : itemId && <DineOpsPairPanel itemId={itemId} /> },
    { id: 'soldin', title: 'Sold in', badge: soldInMark(outlets), content: soldInContent },
  ];

  const blockers: ShellBlocker[] = [
    ...(!locked && outlets && sellingCount === 0 ? [{ label: 'Not sold in any outlet', sectionId: 'soldin' }] : []),
    ...(unrouted.length ? [{ label: `Station not used at ${names(unrouted)}`, sectionId: 'kitchen' }] : []),
    ...(untaxed.length ? [{ label: `No tax profile at ${names(untaxed)}`, sectionId: 'tax' }] : []),
  ];

  const group = groups.find((g) => g.id === details.groupId);

  return (
    <>
      <SectionedModalShell
        title={locked ? 'Add a dish' : `Dish — ${details.itemName || 'dish'}${details.itemCode ? ` (${details.itemCode})` : ''}`}
        summary={(
          <>
            {group && <ShellChip tone="info">{group.courseName} · {group.groupName}</ShellChip>}
            <ShellChip tone="info">{details.isOpenPrice ? 'Open priced' : details.price.trim() ? `Price ${Number(details.price).toFixed(2)}` : 'No price'}</ShellChip>
            {mode === 'edit' && <ShellChip tone={details.isActive ? 'ok' : 'warn'}>{details.isActive ? 'Active' : 'Inactive'}</ShellChip>}
            {!locked && outlets && (
              <ShellChip tone={sellingCount ? 'info' : 'warn'}>{sellingCount ? `Sold in ${sellingCount} outlet${sellingCount === 1 ? '' : 's'}` : 'In no outlet'}</ShellChip>
            )}
            {details.outsideGst && <ShellChip tone="info">Outside GST</ShellChip>}
          </>
        )}
        banners={saveError && <div className="form-error-message" data-tab-error role="alert" style={{ marginBottom: 12 }}>{saveError}</div>}
        sections={sections}
        activeSection={section}
        onSectionChange={(id) => goTo(id as DishSection)}
        onClose={handleClose}
        busy={saving}
        loading={{ active: saving, label: 'Saving...' }}
        maxWidth="1100px"
        footer={(
          <>
            {locked
              ? <span style={{ fontSize: 12, opacity: 0.75, marginRight: 'auto' }}>Sizes, Add-ons, Attributes, Photos, Goes well with and Sold in open once the dish is saved.</span>
              : <ShellBlockers blockers={blockers} onJump={(id) => goTo(id as DishSection)} />}
            <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Close</button>
            <button
              className="btn btn-primary"
              disabled={saving || (!locked && !detailsDirty)}
              title={!locked && !detailsDirty ? 'No changes to Details, Kitchen or Tax' : undefined}
              onClick={() => void saveDetails()}
            >
              {saving ? 'Saving…' : locked ? 'Save and continue' : 'Save'}
            </button>
          </>
        )}
      />
      <ConfirmDialog open={discardOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }} onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

/** Says where an answer came from, because inherited and own look the same otherwise. */
const SourceBadge: React.FC<{ source: string; inheritable: boolean }> = ({ source, inheritable }) => {
  if (source === 'Own') return <Badge text="set on this dish" />;
  if (source === 'Inherited') return <Badge text="inherited from the group" />;
  if (source === 'None') return <Badge text="none — does not inherit" danger />;
  return <Badge text={inheritable ? 'not answered — will inherit' : 'not answered'} muted />;
};

const Badge: React.FC<{ text: string; danger?: boolean; muted?: boolean }> = ({ text, danger, muted }) => (
  <span
    style={{
      marginLeft: 8,
      fontSize: 11,
      fontWeight: 500,
      padding: '1px 6px',
      borderRadius: 10,
      opacity: muted ? 0.6 : 1,
      color: danger ? 'var(--color-danger, #c0392b)' : 'var(--text-secondary, #666)',
      border: `1px solid ${danger ? 'var(--color-danger, #c0392b)' : 'var(--border-color, #ddd)'}`,
    }}
  >
    {text}
  </span>
);

export default DineOpsItemModal;
